// src/components/crm/LeadContactsPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, TextInput } from "flowbite-react";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineStar,
  HiStar,
  HiPencil,
  HiCheck,
  HiX,
} from "react-icons/hi";
import { FiPhoneCall } from "react-icons/fi";
import { IoLogoWhatsapp } from "react-icons/io5";
import { MdOutlineMailOutline, MdOutlineLink } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { LEADS } from "../../config/apiConfig";

const CONTACT_TYPE_LABEL = {
  email: "E-mail",
  phone: "Telefone",
  whatsapp: "WhatsApp",
  website: "Website",
  other: "Outro",
};

const contactIcon = (type, size = 16) => {
  switch (type) {
    case "email":
      return <MdOutlineMailOutline className="text-orange-500" size={size} />;
    case "phone":
      return <FiPhoneCall className="text-blue-600" size={size} />;
    case "whatsapp":
      return <IoLogoWhatsapp className="text-green-600" size={size} />;
    case "website":
      return <MdOutlineLink className="text-cyan-600" size={size} />;
    default:
      return <HiPencil className="text-gray-600" size={size} />;
  }
};

export default function LeadContactsPanel({
  leadId,
  className = "",
  dense = false, // se true, usa espaçamento mais compacto (bom p/ mobile)
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({
    type: "email",
    label: "",
    value: "",
    isPrimary: false,
  });

  // edição inline (um por vez)
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({
    type: "email",
    label: "",
    value: "",
  });

  const canOpenLink = (c) => {
    if (!c?.value) return null;
    switch (c.type) {
      case "email":
        return `mailto:${c.value}`;
      case "phone":
        return `tel:${String(c.value).replace(/\D/g, "")}`;
      case "whatsapp": {
        const phone = String(c.value).replace(/\D/g, "");
        return phone ? `https://wa.me/${phone}` : null;
      }
      case "website": {
        const v = String(c.value).trim();
        if (!v) return null;
        return /^https?:\/\//i.test(v) ? v : `https://${v}`;
      }
      default:
        return null;
    }
  };

  const load = async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(LEADS.CONTACTS.LIST(leadId));
      const list = Array.isArray(data?.data) ? data.data : [];
      list.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
      setContacts(list);
    } catch (e) {
      console.error("Erro ao carregar contatos:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setContacts([]);
    setShowAdd(false);
    setEditingId(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const onAdd = async () => {
    const payload = {
      type: newContact.type || "other",
      value: (newContact.value || "").trim(),
      label: (newContact.label || "").trim() || null,
      isPrimary: !!newContact.isPrimary,
      status: "active",
    };
    if (!payload.value) {
      return window.alert("Informe o valor do contato.");
    }
    try {
      await axios.post(LEADS.CONTACTS.ADD(leadId), payload);
      setShowAdd(false);
      setNewContact({ type: "email", label: "", value: "", isPrimary: false });
      await load();
    } catch (e) {
      console.error("Erro ao adicionar contato:", e);
      window.alert("Não foi possível adicionar o contato.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remover este contato?")) return;
    try {
      await axios.delete(LEADS.CONTACTS.DELETE(leadId, id));
      await load();
    } catch (e) {
      console.error("Erro ao remover contato:", e);
      window.alert("Não foi possível remover o contato.");
    }
  };

  const onSetPrimary = async (id) => {
    try {
      await axios.patch(LEADS.CONTACTS.SET_PRIMARY(leadId, id));
      await load();
    } catch (e) {
      console.error("Erro ao marcar como primário:", e);
      window.alert("Não foi possível marcar como primário.");
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEdit({ type: c.type, label: c.label || "", value: c.value || "" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEdit({ type: "email", label: "", value: "" });
  };
  const saveEdit = async (c) => {
    const payload = {
      type: edit.type || c.type,
      label: (edit.label || "").trim() || null,
      value: (edit.value || "").trim(),
    };
    if (!payload.value) {
      return window.alert("Informe o valor do contato.");
    }
    try {
      await axios.patch(LEADS.CONTACTS.UPDATE(leadId, c.id), payload);
      await load();
      cancelEdit();
    } catch (e) {
      console.error("Erro ao atualizar contato:", e);
      window.alert("Não foi possível atualizar o contato.");
    }
  };

  // layout helpers
  const Row = ({ c, compact = false }) => {
    const link = canOpenLink(c);
    const isEditing = editingId === c.id;

    if (isEditing) {
      return (
        <div
          className={`flex flex-col gap-2 py-2 ${compact ? "" : "border-t"}`}
        >
          <div className="flex flex-col gap-2 md:w-40">
            <Select
              value={edit.type}
              onChange={(e) => setEdit((s) => ({ ...s, type: e.target.value }))}
            >
              <option value="email">E-mail</option>
              <option value="phone">Telefone</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="website">Website</option>
              <option value="other">Outro</option>
            </Select>
          </div>
          <div className="md:flex-1">
            <TextInput
              placeholder="valor"
              value={edit.value}
              onChange={(e) =>
                setEdit((s) => ({ ...s, value: e.target.value }))
              }
            />
          </div>
          <div className="md:w-48">
            <TextInput
              placeholder="rótulo (opcional)"
              value={edit.label}
              onChange={(e) =>
                setEdit((s) => ({ ...s, label: e.target.value }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="xs" onClick={() => saveEdit(c)}>
              <HiCheck size={16} />
              <span className="ml-1">Salvar</span>
            </Button>
            <Button size="xs" color="light" onClick={cancelEdit}>
              <HiX size={16} />
              <span className="ml-1">Cancelar</span>
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={`flex items-start flex-col gap-2 py-1`}>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex gap-2 items-center">
              {c.type ? contactIcon(c.type) : <span>{c.type}</span>}{" "}
              <span className="text-gray-500">{c.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {c.isPrimary ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-yellow-700 bg-yellow-50 border border-yellow-200 px-1.5 py-0.5 rounded">
                  <HiStar size={12} /> primário
                </span>
              ) : null}
              <span className="font-medium">{c.value} </span>
            </div>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
              title="Abrir"
            >
              Abrir
            </a>
          )}
          {!c.isPrimary && (
            <button
              type="button"
              title="Definir como primário"
              onClick={() => onSetPrimary(c.id)}
              className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
            >
              <HiOutlineStar size={14} />
            </button>
          )}
          <button
            type="button"
            title="Editar"
            onClick={() => startEdit(c)}
            className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
          >
            <HiPencil size={14} />
          </button>
          <button
            type="button"
            title="Remover"
            onClick={() => onDelete(c.id)}
            className="px-2 py-1 rounded border text-xs text-red-600 hover:bg-red-50"
          >
            <HiOutlineTrash size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-semibold text-gray-800">
          Contatos do cliente ({contacts?.length})
        </h5>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs bg-white hover:bg-gray-50"
        >
          <HiOutlinePlus size={14} /> Adicionar
        </button>
      </div>

      {showAdd && (
        <div
          className={`mb-3 ${
            dense ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-2"
          }`}
        >
          <Select
            value={newContact.type}
            onChange={(e) =>
              setNewContact((s) => ({ ...s, type: e.target.value }))
            }
          >
            <option value="email">E-mail</option>
            <option value="phone">Telefone</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="website">Website</option>
            <option value="other">Outro</option>
          </Select>
          <TextInput
            placeholder="Categoria (ex.: comercial)"
            value={newContact.label}
            onChange={(e) =>
              setNewContact((s) => ({ ...s, label: e.target.value }))
            }
          />
          <TextInput
            placeholder="Valor"
            value={newContact.value}
            onChange={(e) =>
              setNewContact((s) => ({ ...s, value: e.target.value }))
            }
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="xs" onClick={onAdd}>
              Salvar
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => {
                setShowAdd(false);
                setNewContact({
                  type: "email",
                  label: "",
                  value: "",
                  isPrimary: false,
                });
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Carregando contatos…</div>
      ) : contacts.length ? (
        <div className={`rounded-md ${dense ? "divide-y" : ""}`}>
          {contacts.map((c, i) => (
            <Row key={c.id} c={c} compact={dense} />
          ))}
        </div>
      ) : (
        <div className="text-sm italic text-gray-400">
          Nenhum contato cadastrado.
        </div>
      )}
    </div>
  );
}
